import React from "react";
import { Link } from "react-router-dom";
import { SITE_CONTENT } from "../../constants/siteContent";
import { trackCtaClick } from "../../utils/analytics";
import Shards from "../anim/Shards";
import Topo from "../anim/Topo";
import Icon from "../icons/Icon";
import Section from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import styles from "./Hero.module.css";

/*
 * Hero — paper. Three layers, back to front:
 *   1. the cursor-reactive contour field (<Topo variant="hero">)
 *   2. the centrepiece: the client-facing line (hero.headline) set huge across
 *      the middle, wrapped by <Shards> — an orb of shards that bursts into a
 *      drifting particle field
 *      (the graffico.it hero, in our palette and type)
 *   3. three small blocks on the bottom edge: a status ticket (left), a scroll
 *      cue (centre), a facts block (right)
 *
 * The page's one <h1> lives in Manifesto, so the giant line is NOT a heading:
 * it is a <Statement as="p"> — real text a screen reader reads, in the
 * headline voice. The section is named with aria-label instead.
 *
 * No <Reveal> in here, deliberately: Reveal's observer ignores the bottom 8% of
 * the viewport, which is exactly where these blocks live, so they would stay
 * invisible until the first scroll. The entrance is pure CSS instead, held while
 * <html> carries "is-preloading" (see Hero.module.css).
 *
 * Honesty note: nothing is invented. The ticket renders only while Zeta is
 * genuinely listed on a store, and every fact line is read verbatim out of
 * SITE_CONTENT. There is no founding year in SITE_CONTENT, so there is none
 * here — do not add an "EST." / "SINCE" line, a metric or an award.
 */

/* TODO(content): UI labels only. The facts themselves are edited in
   src/constants/siteContent.js (hero.eyebrow, heroBadges, locations.offices,
   ownProduct), not here. */
const SECTION_LABEL = "Introduction";
const LIVE_LABEL = "Now live";
const STORE_LABEL = "Google Play";
const SCROLL_LABEL = "Scroll";

const LIVE_LABEL_ID = "hero-status-label";

/* The centre line, broken into lines after each comma. The line itself is
   edited in siteContent.js (hero.headline); every claim in it must stay backed
   by a source line there. TODO(content): which words set in the serif accent
   (lower-case, whole words, punctuation ignored; keep it to one or two). */
const HEADLINE = String((SITE_CONTENT.hero || {}).headline || "").trim();
const HEADLINE_LINES = HEADLINE.split(/,\s+/).map((line, index, all) =>
  index < all.length - 1 ? `${line},` : line
);
const ACCENT_WORDS = ["weeks"];

/* Wraps the accent words in <em> (the serif voice), punctuation left outside
   in the sans — the same rule as Manifesto. */
function renderAccented(text) {
  const tokens = text.split(/\s+/).filter(Boolean);
  const nodes = [];
  tokens.forEach((token, index) => {
    const [, before, word, after] = /^([^A-Za-z0-9]*)(.*?)([^A-Za-z0-9]*)$/.exec(token);
    const gap = index < tokens.length - 1 ? " " : "";
    if (word && ACCENT_WORDS.includes(word.toLowerCase())) {
      nodes.push(before, <em key={index}>{word}</em>, `${after}${gap}`);
    } else {
      nodes.push(`${token}${gap}`);
    }
  });
  return nodes;
}

/* Zeta, Appinfigo's own app. The ticket shows only once the product is actually
   live on Google Play — before that there is nothing to send a visitor to. */
const ZETA = SITE_CONTENT.ownProduct || {};
const ZETA_LIVE = ZETA.status === "live" && Boolean(ZETA.playStoreUrl);

/* What we do, what we are, where we are — and nothing SITE_CONTENT cannot back. */
function getFacts(content) {
  const hero = content.hero || {};
  const badges = Array.isArray(content.heroBadges) ? content.heroBadges : [];
  const offices =
    content.locations && Array.isArray(content.locations.offices)
      ? content.locations.offices
      : [];

  return [hero.eyebrow, badges[0], offices[0]].filter(Boolean);
}

function Hero() {
  const facts = getFacts(SITE_CONTENT);

  return (
    <Section
      id="hero"
      tone="paper"
      size="screen"
      container="none"
      contour={false}
      className={styles.hero}
      aria-label={SECTION_LABEL}
    >
      {/* The hero draws its own contour layer (hence contour={false}): the
          canvas variant pauses off-screen and on a hidden tab, and falls back
          to the static SVG under prefers-reduced-motion. */}
      <Topo tone="paper" variant="hero" crop="start" />

      <Shards className={styles.stage}>
        {HEADLINE ? (
          <Statement
            as="p"
            size="statement"
            align="center"
            className={styles.headline}
          >
            {HEADLINE_LINES.map((line) => (
              <span key={line} className={styles.line}>
                {renderAccented(line)}
              </span>
            ))}
          </Statement>
        ) : null}
      </Shards>

      <div className={styles.base}>
        {ZETA_LIVE ? (
          <div className={`${styles.corner} ${styles.status}`}>
            <MicroLabel id={LIVE_LABEL_ID} className={styles.liveLabel}>
              <span className={styles.dot} aria-hidden="true" />
              {LIVE_LABEL}
            </MicroLabel>

            <TicketCard
              as={Link}
              to="/zeta"
              tone="paper"
              className={styles.ticket}
              aria-describedby={LIVE_LABEL_ID}
              onClick={() =>
                trackCtaClick(`${ZETA.name} status card`, "hero", "/zeta")
              }
            >
              {/* The dark-on-light logo: this surface is paper. Decorative —
                  the product name is set in text right below it. */}
              {ZETA.logo ? (
                <span className={styles.logoBox}>
                  <img
                    className={styles.logo}
                    src={`${process.env.PUBLIC_URL}${ZETA.logo}`}
                    alt=""
                    decoding="async"
                    draggable="false"
                  />
                </span>
              ) : null}

              <span className={styles.meta}>
                <span className={styles.nameRow}>
                  <span className={styles.name}>{ZETA.name}</span>
                  <Icon name="arrow-right" size={16} className={styles.arrow} />
                </span>
                <MicroLabel className={styles.store}>{STORE_LABEL}</MicroLabel>
              </span>
            </TicketCard>
          </div>
        ) : null}

        {/* A visual cue only — it says nothing a screen reader needs. */}
        <div className={`${styles.corner} ${styles.cue}`} aria-hidden="true">
          <MicroLabel>{SCROLL_LABEL}</MicroLabel>
          <Icon name="chevron-down" size={14} className={styles.cueIcon} />
        </div>

        {facts.length ? (
          <ul className={`${styles.corner} ${styles.facts}`}>
            {facts.map((fact, index) => (
              <MicroLabel
                as="li"
                key={fact}
                className={index === 0 ? styles.factLead : undefined}
              >
                {fact}
              </MicroLabel>
            ))}
          </ul>
        ) : null}
      </div>
    </Section>
  );
}

export default Hero;
